import { orderBy } from 'lodash';
import { BuildTypeCode } from '../../../../projects/project';
import { FloorLevelCode, getFloorLevelOrderingPosition } from '../../quoteOrDesignFormData';
import { getDistinctFloorLevelCodes, UnitFormModel } from '../../units/unitFormModel';
import { RoomType } from '../roomTypes';
import { DownlossTypeCode } from './downlossTypeCode';

export type RoomPositionalHeatingInformation = {
  heatedBelowPercentage: number;
  temperatureAboveInDegreesCelsius: number;
  downlossTypeCode: DownlossTypeCode;
};

export const maximumDefaultTemperatureAboveInDegreesCelsius = 16;
export const defaultGroundBelowTemperatureInDegreesCelsius = 10;
export const defaultRoomTemperatureInDegreesCelsius = 16;

export const getRoomPositionalHeatingDefaults = (
  roomFloorLevelCode: FloorLevelCode | null,
  externalAirTemperatureInDegreesCelsius: number,
  unit: UnitFormModel,
): RoomPositionalHeatingInformation => {
  const distinctFloorLevelCodesInOrder = orderBy(getDistinctFloorLevelCodes(unit), getFloorLevelOrderingPosition);

  if (distinctFloorLevelCodesInOrder.length === 1) {
    // Single Storey
    return {
      heatedBelowPercentage: 0,
      temperatureAboveInDegreesCelsius: externalAirTemperatureInDegreesCelsius,
      downlossTypeCode: 'GroundBelow',
    };
  }

  const indexOfRoomFloorLevelCode = distinctFloorLevelCodesInOrder.indexOf(roomFloorLevelCode);

  if (indexOfRoomFloorLevelCode === 0) {
    // Lowest in this Unit
    return {
      heatedBelowPercentage: 0,
      temperatureAboveInDegreesCelsius: maximumDefaultTemperatureAboveInDegreesCelsius,
      downlossTypeCode: 'GroundBelow',
    };
  } else if (indexOfRoomFloorLevelCode === distinctFloorLevelCodesInOrder.length - 1) {
    // Highest in this Unit
    return {
      heatedBelowPercentage: 100,
      temperatureAboveInDegreesCelsius: externalAirTemperatureInDegreesCelsius,
      downlossTypeCode: 'RoomBelow',
    };
  } else {
    // Any of the middle floors
    return {
      heatedBelowPercentage: 100,
      temperatureAboveInDegreesCelsius: maximumDefaultTemperatureAboveInDegreesCelsius,
      downlossTypeCode: 'RoomBelow',
    };
  }
};

export const defaultTemperatureBelowInDegreesCelsiusByDownlossTypeCode: {
  [downlossTypeCode in DownlossTypeCode]: number;
} = {
  GroundBelow: 10, // TODO QDT-49
  VoidBelow: -3,
  RoomBelow: 16,
};

type RoomAirChangeRateInformation = {
  [buildTypeCode in BuildTypeCode]: { NumberOfAirChangesPerHour: number; mvhrEfficiencyInPercentage: number };
};

export const defaultRoomAirChangeRatesByRoomType: { [roomTypeCode in RoomType]: RoomAirChangeRateInformation } = {
  Bathroom: {
    NewBuild: { NumberOfAirChangesPerHour: 2, mvhrEfficiencyInPercentage: 0 },
    Retrofit: { NumberOfAirChangesPerHour: 2, mvhrEfficiencyInPercentage: 0 },
  },
  Bedroom: {
    NewBuild: { NumberOfAirChangesPerHour: 0.5, mvhrEfficiencyInPercentage: 0 },
    Retrofit: { NumberOfAirChangesPerHour: 1, mvhrEfficiencyInPercentage: 0 },
  },
  Conservatory: {
    NewBuild: { NumberOfAirChangesPerHour: 1, mvhrEfficiencyInPercentage: 0 },
    Retrofit: { NumberOfAirChangesPerHour: 1, mvhrEfficiencyInPercentage: 0 },
  },
  'Hall/Landing': {
    NewBuild: { NumberOfAirChangesPerHour: 1.5, mvhrEfficiencyInPercentage: 0 },
    Retrofit: { NumberOfAirChangesPerHour: 1.5, mvhrEfficiencyInPercentage: 0 },
  },
  Kitchen: {
    NewBuild: { NumberOfAirChangesPerHour: 1.5, mvhrEfficiencyInPercentage: 0 },
    Retrofit: { NumberOfAirChangesPerHour: 2, mvhrEfficiencyInPercentage: 0 },
  },
  'Dining/Living/Lounge/Sitting': {
    NewBuild: { NumberOfAirChangesPerHour: 1, mvhrEfficiencyInPercentage: 0 },
    Retrofit: { NumberOfAirChangesPerHour: 1.5, mvhrEfficiencyInPercentage: 0 },
  },
  Utility: {
    NewBuild: { NumberOfAirChangesPerHour: 1, mvhrEfficiencyInPercentage: 0 },
    Retrofit: { NumberOfAirChangesPerHour: 2, mvhrEfficiencyInPercentage: 0 },
  },
  WC: {
    NewBuild: { NumberOfAirChangesPerHour: 1.5, mvhrEfficiencyInPercentage: 0 },
    Retrofit: { NumberOfAirChangesPerHour: 2, mvhrEfficiencyInPercentage: 0 },
  },
};
